// `maxScore` is the highest total a submission can reach, so a score can be
// reported as "20 / 40" rather than a bare number a reviewer has to interpret.
//
// questionnaire-service sums the numeric responses into `score` but never
// reports a maximum, so the totals are stated here. Only the questionnaires it
// actually scores carry one; the rest report `score: null`.
//
// CAT is 8 questions at 0–5. CAAT reuses the same question set. The weekly
// check-in asks two questions of everyone plus exactly one gated on the
// patient's respiratory-condition variant, each 0–3, so nine is the most any
// patient can reach — not the fifteen that summing every defined question
// would suggest. R-Scale is 5 required questions at 0–10.
//
// ACT (0–20) is also summed by the service and could be added here; it is left
// bare until product confirms the reported total.
const QUESTIONNAIRE_TYPE_DEFINITIONS = {
  cat: {
    labelKey: "studies.questionnaires.cat",
    labelDefault: "CAT",
    maxScore: 40,
  },
  caat: {
    labelKey: "studies.questionnaires.caat",
    labelDefault: "CAAT",
    maxScore: 40,
  },
  sgrq: {
    labelKey: "studies.questionnaires.sgrq",
    labelDefault: "SGRQ",
  },
  "r-scale": {
    labelKey: "studies.questionnaires.rScale",
    labelDefault: "R-Scale",
    maxScore: 50,
  },
  act: {
    labelKey: "studies.questionnaires.act",
    labelDefault: "ACT",
  },
  "weekly-check-in": {
    labelKey: "studies.questionnaires.weeklyCheckIn",
    labelDefault: "Weekly Check In",
    maxScore: 9,
  },
  "monthly-check-in": {
    labelKey: "studies.questionnaires.monthlyCheckIn",
    labelDefault: "Monthly Check In",
  },
};

const MEASUREMENT_MODE_DEFINITIONS = {
  RecordingSpirometry: {
    modeValue: "spirometry",
    labelKey: "studies.recordingModes.spirometry",
    labelDefault: "Spirometry",
  },
  RecordingPostExertion: {
    modeValue: "post-exertion",
    labelKey: "studies.recordingModes.postExertion",
    labelDefault: "Exertion Recovery",
  },
  RecordingSixMinuteWalk: {
    modeValue: "six-minute-walk",
    labelKey: "studies.recordingModes.sixMinuteWalk",
    labelDefault: "6-Minute Walk",
  },
  RecordingManualAudio: {
    modeValue: "manual-audio",
    labelKey: "studies.recordingModes.manualAudio",
    labelDefault: "Manual Audio",
  },
  RecordingTidalBreathing: {
    modeValue: "tidal-breathing",
    labelKey: "studies.recordingModes.cardiopulmonary",
    labelDefault: "Cardiopulmonary",
  },
  RecordingJapanCOPDLongitudinalStudy: {
    modeValue: "tidal-breathing",
    labelKey: "studies.recordingModes.cardiopulmonary",
    labelDefault: "Cardiopulmonary",
  },
  RecordingCardiopulmonary: {
    modeValue: "tidal-breathing",
    labelKey: "studies.recordingModes.cardiopulmonary",
    labelDefault: "Cardiopulmonary",
  },
};

function normalizeOptionalString(value) {
  if (value === undefined || value === null) {
    return "";
  }

  const normalizedValue = String(value).trim();
  return normalizedValue.length > 0 ? normalizedValue : "";
}

function sortEntriesByTakenAtDescending(entries) {
  return [...entries].sort((left, right) => {
    const leftMs = Date.parse(left?.takenAt || "");
    const rightMs = Date.parse(right?.takenAt || "");

    if (
      Number.isFinite(leftMs) &&
      Number.isFinite(rightMs) &&
      leftMs !== rightMs
    ) {
      return rightMs - leftMs;
    }

    if (Number.isFinite(rightMs)) {
      return 1;
    }

    if (Number.isFinite(leftMs)) {
      return -1;
    }

    return String(left?.docId || left?.name || "").localeCompare(
      String(right?.docId || right?.name || ""),
    );
  });
}

function toTitleCaseToken(token) {
  if (!token) {
    return "";
  }

  if (token === token.toUpperCase() && /[A-Z]/.test(token)) {
    return token;
  }

  const normalizedToken = token.toLowerCase();
  return normalizedToken.charAt(0).toUpperCase() + normalizedToken.slice(1);
}

export function buildQuestionnaireHistoryEntries(questionnaires) {
  return sortEntriesByTakenAtDescending(
    Object.entries(questionnaires || {}).flatMap(
      ([questionnaireType, group]) => {
        const entries = Array.isArray(group?.entries) ? group.entries : [];
        return entries.map((entry) => ({
          ...entry,
          questionnaireType,
        }));
      },
    ),
  );
}

export function countQuestionnaireEntries(questionnaires) {
  return Object.values(questionnaires || {}).reduce((total, group) => {
    if (typeof group?.count === "number" && Number.isFinite(group.count)) {
      return total + group.count;
    }

    const entries = Array.isArray(group?.entries) ? group.entries : [];
    return total + entries.length;
  }, 0);
}

export function getQuestionnaireTypeDefinition(questionnaireType) {
  const normalizedType =
    normalizeOptionalString(questionnaireType).toLowerCase();
  return normalizedType
    ? QUESTIONNAIRE_TYPE_DEFINITIONS[normalizedType] || null
    : null;
}

/**
 * The highest score a submission of `questionnaireType` can reach.
 *
 * @param {string} questionnaireType
 * @returns {number|null} the total, or null when the type is not scored
 */
export function getQuestionnaireMaxScore(questionnaireType) {
  return getQuestionnaireTypeDefinition(questionnaireType)?.maxScore ?? null;
}

/**
 * Splits a questionnaire score into its display parts, so a caller can style
 * the score and its total differently — the score carries the emphasis, the
 * "/ 40" reads as the quieter reference it is.
 *
 * @param {unknown} score
 * @param {string} questionnaireType
 * @returns {{ score: number, maxScore: number|null }|null} null when there is
 *   no numeric score to show
 */
export function getQuestionnaireScoreParts(score, questionnaireType) {
  if (typeof score !== "number" || !Number.isFinite(score)) {
    return null;
  }

  return {
    score,
    maxScore: getQuestionnaireMaxScore(questionnaireType),
  };
}

export function formatQuestionnaireTypeFallback(questionnaireType) {
  const normalizedType = normalizeOptionalString(questionnaireType);

  if (!normalizedType) {
    return "--";
  }

  return normalizedType
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map(toTitleCaseToken)
    .join(" ");
}

export function getMeasurementModeDefinition(entry) {
  const variant = normalizeOptionalString(
    entry?.variant || entry?.collectionName,
  );

  if (!variant) {
    return null;
  }

  return MEASUREMENT_MODE_DEFINITIONS[variant] || null;
}

export function formatMeasurementModeFallback(entry) {
  const variant = normalizeOptionalString(
    entry?.variant || entry?.collectionName,
  );

  if (!variant) {
    return "--";
  }

  const normalizedVariant = variant.startsWith("Recording")
    ? variant.slice("Recording".length)
    : variant;

  return normalizedVariant
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[-_]+/g, " ")
    .trim();
}
